import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTransactions } from "@/hooks/use-transactions";
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from "date-fns";
import { getChartColors } from "@/lib/utils";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

interface IncomeExpensesChartProps {
  isLoading?: boolean;
}

export function IncomeExpensesChart({ isLoading = false }: IncomeExpensesChartProps) {
  const [chartData, setChartData] = useState<any[]>([]);
  
  // Get last 6 months range
  const today = new Date();
  const sixMonthsAgo = subMonths(today, 5);
  const monthRange = eachMonthOfInterval({
    start: startOfMonth(sixMonthsAgo),
    end: endOfMonth(today),
  });
  
  // Format dates for API
  const startDateStr = format(startOfMonth(sixMonthsAgo), "yyyy-MM-dd");
  const endDateStr = format(endOfMonth(today), "yyyy-MM-dd");
  
  // Get transactions for the last 6 months (business scoped via useTransactions context)
  const { transactions } = useTransactions(undefined, {
    startDate: startDateStr,
    endDate: endDateStr,
    limit: 1000, // Setting a high limit to get all transactions
  });
  
  useEffect(() => {
    // Always rebuild chart data — empty transactions = zeroed chart (no stale data)
    const data = monthRange.map((month) => ({
      month: format(month, "MMM"),
      income: 0,
      expenses: 0,
    }));

    if (transactions && transactions.length) {
      transactions.forEach((transaction) => {
        const transactionDate = new Date(transaction.date);
        const monthIndex = monthRange.findIndex(
          (m) =>
            m.getMonth() === transactionDate.getMonth() &&
            m.getFullYear() === transactionDate.getFullYear()
        );
        if (monthIndex !== -1) {
          const amount = parseFloat(transaction.amount.toString());
          if (transaction.type === "income") {
            data[monthIndex].income += amount;
          } else {
            data[monthIndex].expenses += amount;
          }
        }
      });
    }

    setChartData(data);
  }, [transactions]);
  
  const chartColors = getChartColors();
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium">Income vs Expenses</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-80 w-full bg-neutral-100 animate-pulse rounded-md"></div>
        </CardContent>
      </Card>
    );
  }
  
  if (!chartData.length) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium">Income vs Expenses</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-80 flex items-center justify-center">
            <p className="text-neutral-500">No data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium">Income vs Expenses</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis 
                tickFormatter={(value) => 
                  new Intl.NumberFormat("en-US", {
                    notation: "compact",
                    compactDisplay: "short",
                  }).format(value)
                }
              />
              <Tooltip 
                formatter={(value) => 
                  new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(Number(value))
                }
              />
              <Legend />
              <Bar dataKey="income" name="Income" fill={chartColors[0]} />
              <Bar dataKey="expenses" name="Expenses" fill={chartColors[2]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}